import { FC, PropsWithChildren, ReactNode } from 'react'

import { styled } from 'styled-components'

import FormLabel from 'core/components/lib/FormLabel'
import Tooltip from 'core/components/lib/Tooltip'
import { useRect, useBorrowerIsMobile } from 'core/hooks'
import { onSurface, onSurfaceMutedVariant, onSurfaceVariant } from 'core/styles'

type CardSectionProps = PropsWithChildren<{
  alignItems?: string
  alignLabel?: string
  label?: string
  /** Only provide this if you need to override the
   * default margin of 24px for some special use case.
   */
  margin?: string
  shortLabel?: boolean
  subLabel?: string
  labelWidth?: string
  marginRight?: string
  tooltip?: ReactNode
  variant?: 'compact'
}>

const CardSection: FC<CardSectionProps> = (props) => {
  const {
    alignItems,
    alignLabel,
    children,
    label,
    margin = '24px',
    shortLabel,
    subLabel,
    labelWidth,
    marginRight,
    tooltip,
    variant,
  } = props

  const isMobile = useBorrowerIsMobile()
  const [{ width }, ref] = useRect()

  const compact = isMobile || width < 400

  return compact ?
      <BlockContainer ref={ref}>
        {label && <BlockLabel text={label} />}
        <BlockContent>{children}</BlockContent>
      </BlockContainer>
    : <Container $alignItems={alignItems} $margin={margin} $variant={variant} ref={ref}>
        {label && (
          <LabelContainer
            $alignLabel={alignLabel}
            $hasSubLabel={!!subLabel}
            $labelWidth={labelWidth}
            $marginRight={marginRight}
            $shortLabel={shortLabel}
          >
            <FormLabel text={label} />
            {subLabel && <SubLabel text={subLabel} />}
          </LabelContainer>
        )}
        <Content>{children}</Content>
        {!!tooltip && (
          <TooltipBox $alignSelf={alignLabel}>
            <Tooltip content={tooltip} />
          </TooltipBox>
        )}
      </Container>
}

export default CardSection

export const Container = styled.div<{
  $alignItems?: string
  $margin?: string
  $variant?: 'compact'
}>`
  display: flex;
  align-items: ${(p) => p.$alignItems ?? 'flex-start'};
  margin: ${(p) => p.$margin};

  ${(p) =>
    p.$variant === 'compact' &&
    `
      margin: 11px 24px;
      justify-content: space-between;
      align-items: center;
      align-self: stretch;

      label,
      span[data-testid='label'] {
        padding: 8px 0;
        text-align: center;
        line-height: normal;
        color: ${onSurfaceVariant};
        font-family: Inter, sans-serif;
        font-size: 14px;
        font-weight: 500;
        font-style: normal;
      }
  `}
`

const LabelContainer = styled.div<{
  $labelWidth?: string
  $shortLabel?: boolean
  $alignLabel?: string
  $hasSubLabel?: boolean
  $marginRight?: string
}>`
  display: flex;
  position: relative;
  flex: 0 0 ${(p) => p.$labelWidth || (p.$shortLabel ? '40px' : '240px')};
  align-self: ${(p) => p.$alignLabel ?? 'center'};
  transform: ${(p) => (p.$hasSubLabel ? 'translateY(-0.4em)' : 'none')};
  margin-right: ${(p) => (p.$marginRight ? p.$marginRight : '0px')};
`

const SubLabel = styled(FormLabel)`
  position: absolute;
  bottom: 0;
  left: 0;
  transform: translateY(1.5rem);
  color: ${onSurfaceMutedVariant};
`

const Content = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
`

const BlockContainer = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;
  margin-left: 8px;
`

const BlockLabel = styled(FormLabel)`
  display: block;
  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 8px;
`

const BlockContent = styled.div`
  padding: 8px;
  color: ${onSurface};
`

const TooltipBox = styled.div<{
  $alignSelf?: string
}>`
  align-self: ${(p) => p.$alignSelf ?? 'center'};
  margin-left: 16px;
`
